<template>
  <div class="home-page animated fadeIn mt-3">
    <div class="home-grid">
      <!-- Main hero -->
      <div class="hero-block">
        <p class="hero-greeting">{{ timeGreeting }}{{ userName ? `, ${userName}` : "" }}</p>
        <h1 class="hero-title">Admin Panel</h1>
        <p class="hero-date">{{ formattedDate }}</p>
        <div class="hero-decoration">
          <span class="hero-dot"></span>
          <span class="hero-dot"></span>
          <span class="hero-dot"></span>
        </div>
      </div>

      <!-- Sidebar navigation hint -->
      <div class="hint-block">
        <div class="hint-inner">
          <div class="hint-icon-wrap">
            <i class="fa fa-compass"></i>
          </div>
          <div class="hint-content">
            <h3>Navigate with the sidebar</h3>
            <p>Your sidebar shows all sections available to your account.</p>
          </div>
        </div>
      </div>

      <!-- Tip of the Day -->
      <div class="tip-block">
        <div class="tip-inner">
          <div class="tip-header-row">
            <span class="tip-label">Tip of the Day</span>
          </div>
          <p class="tip-content">{{ currentTip }}</p>
        </div>
      </div>

      <!-- Footer cards -->
      <div class="workspace-block">
        <div class="block-icon"><i class="fa fa-th-large"></i></div>
        <h4>Your workspace</h4>
        <p>Sections in your sidebar are tailored to your role and permissions.</p>
      </div>

      <div class="support-block">
        <div class="block-icon"><i class="fa fa-life-ring"></i></div>
        <h4>Need help?</h4>
        <p>Reach out to our support team for assistance.</p>
        <a href="https://trigital.in/contact-us/" target="_blank" rel="noopener" class="support-link">
          Contact Support <i class="fa fa-external-link"></i>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AdminWelcome",
  data() {
    return {
      currentDate: new Date(),
      tipIndex: 0,
      tips: [
        "Use date filters (Today, 7 days, 30 days, or custom range) to compare trends and spot patterns in your data."
      ],
    };
  },
  computed: {
    timeGreeting() {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 12) return "Good morning";
      if (hour >= 12 && hour < 17) return "Good afternoon";
      if (hour >= 17 && hour < 21) return "Good evening";
      return "Welcome"; // 21:00–04:59 — late night / early hours, 24/7 global
    },
    currentTip() {
      return this.tips[this.tipIndex] || this.tips[0];
    },
    userName() {
      const user = this.$currentUserData;
      if (!user) return "";
      return (
        user.authentication?.userName ||
        user.authentication?.email ||
        user.name ||
        ""
      );
    },
    formattedDate() {
      const opts = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      return this.currentDate.toLocaleDateString("en-IN", opts);
    },
  },
};
</script>

<style scoped>
.home-page {
  min-height: 70vh;
  padding: 0;
}

.home-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto 1fr;
  gap: 1.25rem;
  grid-template-areas:
    "hero hero"
    "hint tip"
    "workspace support";
}

/* Hero */
.hero-block {
  grid-area: hero;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  border-radius: 16px;
  padding: 2rem 2.5rem;
  position: relative;
  overflow: hidden;
}

.hero-block::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -10%;
  width: 60%;
  height: 200%;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 29, 94, 0.12) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.hero-greeting {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 0.35rem 0;
  font-weight: 500;
}

.hero-title {
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.03em;
}

.hero-date {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
}

.hero-decoration {
  position: absolute;
  bottom: 1.5rem;
  right: 2rem;
  display: flex;
  gap: 0.5rem;
}

.hero-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
}

.hero-dot:nth-child(1) { background: #ff1d5e; }
.hero-dot:nth-child(2) { background: rgba(255, 255, 255, 0.5); }
.hero-dot:nth-child(3) { background: rgba(255, 255, 255, 0.3); }

/* Hint block */
.hint-block {
  grid-area: hint;
}

.hint-inner {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.25rem;
  height: 100%;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.hint-inner:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.hint-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hint-icon-wrap i {
  font-size: 1.1rem;
  color: #0284c7;
}

.hint-content h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.hint-content p {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

/* Tip block */
.tip-block {
  grid-area: tip;
}

.tip-inner {
  background: linear-gradient(145deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%);
  border: 1px solid #fcd34d;
  border-radius: 14px;
  padding: 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tip-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.tip-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #b45309;
}

.tip-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tip-ctrl {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  color: #b45309;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}

.tip-ctrl:hover {
  background: #fff;
  color: #92400e;
}

.tip-ctrl i {
  font-size: 0.7rem;
}

.tip-num {
  font-size: 0.75rem;
  color: #a16207;
  font-weight: 500;
}

.tip-content {
  font-size: 0.9375rem;
  color: #78350f;
  line-height: 1.6;
  margin: 0;
  flex: 1;
}

/* Workspace & Support blocks */
.workspace-block,
.support-block {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.workspace-block:hover,
.support-block:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.block-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.block-icon i {
  font-size: 1rem;
  color: #6b7280;
}

.support-block .block-icon {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
}

.support-block .block-icon i {
  color: #ff1d5e;
}

.workspace-block h4,
.support-block h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.35rem 0;
}

.workspace-block p,
.support-block p {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 0.75rem 0;
  flex: 1;
}

.support-link {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #ff1d5e;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: auto;
}

.support-link:hover {
  color: #e01a54;
  text-decoration: none;
}

/* Responsive */
@media (max-width: 991.98px) {
  .home-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    grid-template-areas:
      "hero"
      "hint"
      "tip"
      "workspace"
      "support";
  }
}

@media (max-width: 575.98px) {
  .hero-block {
    padding: 1.5rem 1.25rem;
  }

  .hero-title {
    font-size: 1.625rem;
  }

  .hero-decoration {
    bottom: 1rem;
    right: 1.25rem;
  }
}
</style>
